// src/budget/model/program/programBudgetSummary.js
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";

/**
 * Compute the sum of selected currency fields for a given row.
 *
 * This mirrors the original `sumRowCurrency` logic from ProgramBudget.jsx,
 * summing only the keys provided in `sumCurrencyKeys`. If all values are zero
 * or the row is missing, the function returns `null` to signal "no meaningful total."
 *
 * @param {string[]} sumCurrencyKeys
 *   List of field keys whose numeric values should be summed.
 *
 * @param {Object<string, any>} row
 *   A year-row object containing currency values (Approved or Actual).
 *
 * @returns {number|null}
 *   The numeric total of the requested currency fields, or `null` if no values exist.
 */
export function sumRowCurrency(sumCurrencyKeys, row) {
  if (!row) return null;
  let total = 0;
  let hasAny = false;

  for (const key of sumCurrencyKeys) {
    const v = Number(row[key]) || 0;
    if (v !== 0) {
      total += v;
      hasAny = true;
    }
  }

  return hasAny ? total : null;
}

// programBudgetSummary.js
export function getSummaryYearOptions({ approvedRows = [], actualRows = [], proposedRows = [] }) {
  const approved = new Set(approvedRows.map(r => r?.year).filter(Number.isFinite));
  const actual = new Set(actualRows.map(r => r?.year).filter(Number.isFinite));
  const proposed = new Set(proposedRows.map(r => r?.year).filter(Number.isFinite));

  const all = new Set([...approved, ...actual, ...proposed]);
  const years = [...all].sort((a, b) => a - b);

  const isValidNormal = (y) =>
    actual.has(y - 2) &&
    actual.has(y - 1) &&
    approved.has(y - 1) &&
    (approved.has(y) || proposed.has(y));

  const isValidProposedOnly = (y) =>
    proposed.has(y) &&
    !approved.has(y) &&
    actual.has(y - 3) &&
    actual.has(y - 2) &&
    approved.has(y - 1);

  return years.filter(y => isValidNormal(y) || isValidProposedOnly(y));
}

/**
 * Build the column specification for the **one-row summary table**.
 *
 * Columns correspond to:
 *   - Actual for (summaryYear - 2)
 *   - Actual for (summaryYear - 1)
 *   - Approved for (summaryYear - 1)
 *   - Approved for (summaryYear)
 *   - Change (year-over-year difference)
 *   - % Change
 *
 * Returned column configs match the format consumed by <ProgramBudgetSummaryTable>.
 *
 * @param {number} summaryYear
 *   The fiscal year selected by the user (ex: 2025).
 *
 * @returns {Array<Object>}
 *   Array of column config objects, including formatting rules.
 */
export function buildSummaryColumns(summaryYear) {
  if (!summaryYear) return [];

  const y2 = summaryYear - 2;
  const y1 = summaryYear - 1;
  const y0 = summaryYear;

  return [
    {
      key: "label",
      label: "",
      style: { width: "260px", minWidth: "260px" },
      editable: false,
    },
    {
      key: "y2Actual",
      label: `${formatFiscalYearFromStart(y2)} Actual`,
      format: "currency",
      align: "right",
      style: { width: "110px", minWidth: "110px" },
    },
    {
      key: "y1Actual",
      label: `${formatFiscalYearFromStart(y1)} Actual`,
      format: "currency",
      align: "right",
      style: { width: "110px", minWidth: "110px" },
    },
    {
      key: "y1Approved",
      label: `${formatFiscalYearFromStart(y1)} Approved`,
      format: "currency",
      align: "right",
      style: { width: "110px", minWidth: "110px" },
    },
    {
      key: "y0Approved",
      label: `${formatFiscalYearFromStart(y0)} Approved`,
      format: "currency",
      align: "right",
      style: { width: "110px", minWidth: "110px" },
    },
    {
      key: "increase",
      label: "Change",
      format: "currency",
      align: "right",
      style: { width: "110px", minWidth: "110px" },
    },
    {
      key: "percentIncrease",
      label: "% Change",
      format: "percent",
      align: "right",
      style: { width: "90px", minWidth: "90px" },
    },
  ];
}

/**
 * Build the **single summary row** used at the top of every program’s budget page.
 *
 * This uses 3 years of Actual and Approved values to produce:
 *   - y2Actual
 *   - y1Actual
 *   - y1Approved
 *   - y0Approved
 *   - increase (y0Approved − y1Approved)
 *   - percentIncrease ((increase / y1Approved), or null)
 *
 * @param {Object} params
 *   Wrapper object for all inputs.
 *
 * @param {number} params.summaryYear
 *   The fiscal year selected by the user.
 *
 * @param {Map<number, Object>} params.approvedByYear
 *   Map of year → approved data row.
 *
 * @param {Map<number, Object>} params.actualByYear
 *   Map of year → actual data row.
 *
 * @param {string[]} params.sumCurrencyKeys
 *   Currency fields to sum for each row to compute totals.
 *
 * @param {string} params.fullName
 *   The program display name (ex: "ENGLISH / READING").
 *
 * @param {string} params.programId
 *   The program ID (ex: "0400") used as a fallback label.
 *
 * @returns {Object|null}
 *   A row object compatible with ProgramBudgetSummaryTable, or null if no year provided.
 */
export function buildSummaryRow({
  summaryYear,
  approvedByYear,
  actualByYear,
  sumCurrencyKeys,
  fullName,
  programId,
}) {
  if (!summaryYear) return null;

  const y2 = summaryYear - 2;
  const y1 = summaryYear - 1;
  const y0 = summaryYear;

  const y2Actual = sumRowCurrency(sumCurrencyKeys, actualByYear.get(y2));
  const y1Actual = sumRowCurrency(sumCurrencyKeys, actualByYear.get(y1));
  const y1Approved = sumRowCurrency(sumCurrencyKeys, approvedByYear.get(y1));
  const y0Approved = sumRowCurrency(sumCurrencyKeys, approvedByYear.get(y0));

  let increase = null;
  let percentIncrease = null;

  if (y0Approved != null && y1Approved != null) {
    increase = y0Approved - y1Approved;
    percentIncrease = y1Approved !== 0 ? increase / y1Approved : null;
  }

  return {
    label: fullName || programId,
    y2Actual,
    y1Actual,
    y1Approved,
    y0Approved,
    increase,
    percentIncrease,
  };
}